<div class="simulate-redeem-container">
  <div class="split-grid">
    <mat-card class="grid-upload">
      <mat-card-content>
        @if (selectedFiles.length === 0) {
          <div class="upload-section">
            <h3>Upload CSV File</h3>
            <mat-card-subtitle>
              Columns required: user_id, code. Maximum 500 rows. No SafeBox code is consumed during simulation.
            </mat-card-subtitle>

            <ngx-dropzone
              (change)="onFileSelect($event)"
              [accept]="'.csv'"
              [multiple]="false"
              [disabled]="isSimulationRunning"
            >
              <ngx-dropzone-label>
                <mat-icon>cloud_upload</mat-icon>
                <p>Drop CSV file here or click to browse</p>
                <small>Only CSV files are accepted</small>
              </ngx-dropzone-label>
            </ngx-dropzone>

            <div class="template-section">
              <mat-icon>info</mat-icon>
              <span>Don't have a CSV template? </span>
              <lib-button (buttonClick)="downloadTemplate()" color="primary" icon="download">
                Download Template
              </lib-button>
            </div>
          </div>
        } @else {
          <div class="preview-section">
            <div class="preview-header">
              <h3>{{ selectedFiles[0].name }}</h3>
              <lib-button variant="icon" icon="close" matTooltip="Remove file" [disabled]="isSimulationRunning" (buttonClick)="onFileRemove(selectedFiles[0])"></lib-button>
            </div>
            <mat-card-subtitle>{{ previewRows.length }} row{{ previewRows.length === 1 ? '' : 's' }} ready to simulate</mat-card-subtitle>

            <div class="results-table-container">
              <table mat-table [dataSource]="previewRows">
                <ng-container matColumnDef="userId">
                  <th mat-header-cell *matHeaderCellDef>User ID</th>
                  <td mat-cell *matCellDef="let row">{{ row.userId }}</td>
                </ng-container>

                <ng-container matColumnDef="code">
                  <th mat-header-cell *matHeaderCellDef>Code</th>
                  <td mat-cell *matCellDef="let row">{{ row.code }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="previewColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: previewColumns;"></tr>
              </table>
            </div>
          </div>
        }

        <div class="option-row">
          <mat-slide-toggle
            [checked]="checkLimitRule"
            [disabled]="isSimulationRunning"
            (change)="checkLimitRule = $event.checked"
            color="primary"
          >
            <span class="toggle-label">Skip Checking Consumption/Time Limit</span>
          </mat-slide-toggle>
          <mat-icon
            matTooltip="When on, the campaign’s consumption/time limit checks will be skipped for every rows in this job"
            class="info-icon"
          >
            info_outline
          </mat-icon>
        </div>

        <div class="action-buttons">
          <lib-button
            color="primary"
            [disabled]="!canStartSimulation()"
            (buttonClick)="startSimulation()"
            icon="play_arrow"
          >
            Run Simulation
          </lib-button>

          <lib-button (buttonClick)="resetAll()" icon="refresh">
            Reset
          </lib-button>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card class="history-card grid-history">
      <mat-card-header>
        <mat-card-title>Recent Jobs</mat-card-title>
      </mat-card-header>

      <mat-card-content>
        <div class="history-toolbar">
          <span class="history-hint">Click a job to resume watching it</span>
          <lib-button variant="icon" icon="refresh" matTooltip="Refresh list" [disabled]="isLoadingHistory" (buttonClick)="loadJobHistory()"></lib-button>
        </div>

        @if (isLoadingHistory && jobHistory.length === 0) {
          <div class="empty-state">
            <mat-icon color="disabled">hourglass_empty</mat-icon>
            <span>Loading job history...</span>
          </div>
        } @else if (jobHistory.length === 0) {
          <div class="empty-state">
            <mat-icon color="disabled">history</mat-icon>
            <span>No jobs yet - run a simulation to get started.</span>
          </div>
        } @else {
          <div class="history-table-container">
            <table mat-table [dataSource]="jobHistory">
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let job">
                  <span class="status-badge" [ngClass]="historyStatusMeta(job.status).cssClass">
                    <mat-icon [class.spin-icon]="job.status === 'Running'">{{ historyStatusMeta(job.status).icon }}</mat-icon>
                    {{ historyStatusMeta(job.status).label }}
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="progress">
                <th mat-header-cell *matHeaderCellDef>Progress</th>
                <td mat-cell *matCellDef="let job">
                  <div class="history-progress-cell">
                    <mat-progress-bar mode="determinate" [value]="historyRedeemableRatio(job)" [ngClass]="historyProgressColorClass(job)"></mat-progress-bar>
                    <span>{{ historyRedeemableRatio(job) }}%</span>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="rows">
                <th mat-header-cell *matHeaderCellDef>Rows</th>
                <td mat-cell *matCellDef="let job">{{ job.processedRows }} / {{ job.totalRows }}</td>
              </ng-container>

              <ng-container matColumnDef="startTime">
                <th mat-header-cell *matHeaderCellDef>Started</th>
                <td mat-cell *matCellDef="let job">{{ job.startTime | date : "yyyy-MM-dd HH:mm" }}</td>
              </ng-container>

              <ng-container matColumnDef="duration">
                <th mat-header-cell *matHeaderCellDef>Duration</th>
                <td mat-cell *matCellDef="let job">{{ historyDuration(job) }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="historyColumns"></tr>
              <tr
                mat-row
                *matRowDef="let job; columns: historyColumns;"
                class="history-row"
                [class.selected]="job.jobId === selectedHistoryJobId"
                (click)="selectHistoryJob(job)"
              ></tr>
            </table>
          </div>
        }
      </mat-card-content>
    </mat-card>

    <!-- Simulation progress -->
    @if (isSimulationRunning || simulationCompleted || simulationEverStarted) {
      <mat-card class="progress-card grid-sim-progress">
        <mat-card-header>
          <mat-card-title>Simulation Progress</mat-card-title>
        </mat-card-header>

        <mat-card-content>
          <div class="progress-section">
            <div class="progress-header">
              <span>Overall Progress</span>
              <span>{{ simulationProgress.percentage }}%</span>
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="simulationProgress.percentage"
              [color]="getSimulationProgressBarColor()"
            ></mat-progress-bar>
            <div class="progress-details">
              <span>{{ simulationProgress.processed }} / {{ simulationProgress.total }} rows processed</span>
            </div>
          </div>

          <div class="status-section">
            <div class="status-item">
              <mat-icon [color]="simulationProgress.valid > 0 ? 'primary' : ''">check_circle</mat-icon>
              <span>Redeemable: {{ simulationProgress.valid }}</span>
            </div>
            <div class="status-item">
              <mat-icon [color]="simulationProgress.invalid > 0 ? 'warn' : ''">block</mat-icon>
              <span>Not Redeemable: {{ simulationProgress.invalid }}</span>
            </div>
          </div>

          @if (logs.length > 0 || isSimulationRunning || simulationEverStarted) {
            <div class="log-section">
              <div class="log-header">
                <h4>Activity Log</h4>
                @if (logs.length > 0) {
                  <div class="log-controls">
                    @if (isMonitoringSimulation()) {
                      <mat-icon color="accent" matTooltip="Monitoring Active" class="pulse-icon">wifi</mat-icon>
                    }
                    <lib-button variant="icon" color="accent" (buttonClick)="clearLogs()" matTooltip="Clear logs" icon="clear_all"></lib-button>
                    @if (isMonitoringSimulation()) {
                      <lib-button variant="icon" color="warn" (buttonClick)="stopSimulationManually()" matTooltip="Stop monitoring" icon="stop"></lib-button>
                    }
                  </div>
                }
              </div>
              <div class="log-container" #logContainer>
                @for (log of logs; track trackByLogEntry($index, log)) {
                  <div class="log-entry" [ngClass]="'log-' + log.level">
                    <span class="log-timestamp">{{ log.timestamp | date : "HH:mm:ss" }}</span>
                    <span class="log-message">{{ log.message }}</span>
                  </div>
                }
                @if (logs.length === 0) {
                  <div class="log-empty">
                    <mat-icon color="disabled">info</mat-icon>
                    <span>Activity logs will appear here...</span>
                  </div>
                }
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }

    <!-- Simulation results -->
    <mat-card class="results-card grid-sim-results">
      <mat-card-header>
        <mat-card-title>Simulation Results</mat-card-title>
      </mat-card-header>

      <mat-card-content>
        @if (simulationResults.length === 0) {
          <div class="empty-state">
            <mat-icon color="disabled">table_chart</mat-icon>
            <span>No results yet - run a simulation to see redeemable codes here.</span>
          </div>
        } @else {
          <div class="results-toolbar">
            <div class="results-summary">
              <span>{{ simulationResults.length }} rows</span>
              <span class="dot">&middot;</span>
              <span>{{ redeemableRows.length }} redeemable</span>
              <span class="dot">&middot;</span>
              <span>{{ simulationProgress.invalid }} invalid</span>
            </div>
            <div class="results-actions">
              <lib-button variant="icon" icon="download" matTooltip="Download simulation report" (buttonClick)="downloadSimulationReport()"></lib-button>
              @if (simulationProgress.invalid > 0) {
                <lib-button variant="icon" icon="error_outline" color="warn" matTooltip="Download not-redeemable rows" (buttonClick)="downloadSimulationErrors()"></lib-button>
              }
            </div>
          </div>

          <div class="results-table-container">
            <table mat-table [dataSource]="simulationResults">
              <ng-container matColumnDef="select">
                <th mat-header-cell *matHeaderCellDef>
                  <mat-checkbox
                    [checked]="isAllRedeemableSelected()"
                    [disabled]="redeemableRows.length === 0"
                    (change)="masterToggle()"
                  ></mat-checkbox>
                </th>
                <td mat-cell *matCellDef="let row">
                  <mat-checkbox
                    [checked]="isRowSelected(row)"
                    [disabled]="!row.isRedeemable"
                    (change)="toggleRow(row)"
                  ></mat-checkbox>
                </td>
              </ng-container>

              <ng-container matColumnDef="userId">
                <th mat-header-cell *matHeaderCellDef>User ID</th>
                <td mat-cell *matCellDef="let row">{{ row.userId }}</td>
              </ng-container>

              <ng-container matColumnDef="code">
                <th mat-header-cell *matHeaderCellDef>Code</th>
                <td mat-cell *matCellDef="let row">{{ row.code }}</td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let row">
                  <span class="status-badge" [ngClass]="statusMeta(row.status).cssClass">
                    <mat-icon>{{ statusMeta(row.status).icon }}</mat-icon>
                    {{ statusMeta(row.status).label }}
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="reward">
                <th mat-header-cell *matHeaderCellDef>Would Receive</th>
                <td mat-cell *matCellDef="let row">
                  @if (row.isRedeemable) {
                    <span>
                      @if (row.campaignName) { <span>{{ row.campaignName }}</span> }
                      @if (row.rewardSummary) { <span> &middot; {{ row.rewardSummary }}</span> }
                      @if (!row.campaignName && !row.rewardSummary) { <span class="muted">&mdash;</span> }
                    </span>
                  } @else {
                    <span class="muted">&mdash;</span>
                  }
                </td>
              </ng-container>

              <ng-container matColumnDef="message">
                <th mat-header-cell *matHeaderCellDef>Message</th>
                <td mat-cell *matCellDef="let row">{{ row.message }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="resultsColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: resultsColumns;"></tr>
            </table>
          </div>
        }
      </mat-card-content>
    </mat-card>

    <!-- Redeem execution progress -->
    @if (isRedeemRunning || redeemCompleted || redeemEverStarted) {
      <mat-card class="progress-card grid-redeem-progress">
        <mat-card-header>
          <mat-card-title>Redeem Execution Progress</mat-card-title>
        </mat-card-header>

        <mat-card-content>
          <div class="progress-section">
            <div class="progress-header">
              <span>Overall Progress</span>
              <span>{{ redeemProgress.percentage }}%</span>
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="redeemProgress.percentage"
              [color]="getRedeemProgressBarColor()"
            ></mat-progress-bar>
            <div class="progress-details">
              <span>{{ redeemProgress.processed }} / {{ redeemProgress.total }} rows processed</span>
            </div>
          </div>

          <div class="status-section">
            <div class="status-item">
              <mat-icon [color]="redeemProgress.success > 0 ? 'primary' : ''">check_circle</mat-icon>
              <span>Successful: {{ redeemProgress.success }}</span>
            </div>
            <div class="status-item">
              <mat-icon [color]="redeemProgress.failed > 0 ? 'warn' : ''">error</mat-icon>
              <span>Failed: {{ redeemProgress.failed }}</span>
            </div>
            @if (isMonitoringRedeem()) {
              <lib-button variant="icon" color="warn" (buttonClick)="stopRedeemManually()" matTooltip="Stop monitoring" icon="stop"></lib-button>
            }
          </div>

          @if (redeemCompleted) {
            <div class="results-table-container">
              <table mat-table [dataSource]="redeemResults">
                <ng-container matColumnDef="userId">
                  <th mat-header-cell *matHeaderCellDef>User ID</th>
                  <td mat-cell *matCellDef="let row">{{ row.userId }}</td>
                </ng-container>

                <ng-container matColumnDef="code">
                  <th mat-header-cell *matHeaderCellDef>Code</th>
                  <td mat-cell *matCellDef="let row">{{ row.code }}</td>
                </ng-container>

                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef>Status</th>
                  <td mat-cell *matCellDef="let row">
                    <span class="status-badge" [ngClass]="row.success ? 'status-success' : 'status-error'">
                      <mat-icon>{{ row.success ? 'check_circle' : 'error' }}</mat-icon>
                      {{ row.success ? 'Redeemed' : 'Failed' }}
                    </span>
                  </td>
                </ng-container>

                <ng-container matColumnDef="message">
                  <th mat-header-cell *matHeaderCellDef>Message</th>
                  <td mat-cell *matCellDef="let row">{{ row.message }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="redeemResultsColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: redeemResultsColumns;"></tr>
              </table>
            </div>

            <div class="results-section">
              <div class="result-actions">
                <lib-button color="primary" (buttonClick)="downloadRedeemReport()" icon="download">
                  Download Redeem Report
                </lib-button>
                @if (redeemErrorReportS3Url || redeemProgress.failed > 0) {
                  <lib-button color="accent" (buttonClick)="downloadRedeemErrors()" icon="error_outline">
                    Download Error Report
                  </lib-button>
                }
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }
  </div>
</div>
